import React, { useEffect, useState } from 'react';
import { Input, Table, DOMHelper, Stack, IconButton, SelectPicker, Checkbox, Divider, Button } from 'rsuite';
import type { SortType } from 'rsuite';
import EditIcon from '@rsuite/icons/legacy/Edit';
import { BaseErrorModel, PagedResponseModel, PaginationFilterResult } from '@/models/core.model';
import { useNavigate } from 'react-router-dom';
import { AxiosError } from 'axios';
import FetchingSelect from '@/components/FetchingSelect';
import { getCitiesLookup } from '@/services/city.service';
import TablePagination from '@/components/TablePagination';
import { toastErrorMessage } from '@/utils/toasterMessage';
import ImageCell from '@/components/ImageCell';
import { EventTypeList, ScrapedEvent, ScrapedEventPaginationFilterRequest, ScrapSourceList } from '@/models/scraped-event.model';
import { getPagedScrapedEvents } from '@/services/scraped-event.service';
import { jsDateToZone } from '@/utils/date-time';
import { getCountriesLookup } from '@/services/country.service';
import PageContent from '@/components/PageContent';

const { Column, HeaderCell, Cell } = Table;
const { getHeight } = DOMHelper;

const ScrapedEventPage = () => {

  const [filterReq, setFilterReq] = useState<ScrapedEventPaginationFilterRequest>(new ScrapedEventPaginationFilterRequest());
  const [pageRes, setPageRes] = useState<PaginationFilterResult>(new PaginationFilterResult());
  const [items, setItems] = useState<ScrapedEvent[]>([]);
  const [loading, setLoading] = useState(true);

  const nav = useNavigate();

  useEffect(() => {
    fetchData(filterReq);
  }, [filterReq]);

  const fetchData = async (req: ScrapedEventPaginationFilterRequest) => {
    setLoading(true);
    try {
      const result: PagedResponseModel<ScrapedEvent> = await getPagedScrapedEvents(req);
      if (result.success) {
        setItems(result.result);
        setPageRes(pre => ({ 
          ...pre, 
          pageNumber: result.pageNumber, 
          totalRecords: result.totalRecords, 
          pageSize: result.pageSize 
        }));
      }
    } catch (err) {
      setItems([]);
      const errorModel = (err as AxiosError<BaseErrorModel>).response?.data;
      toastErrorMessage(`Error in fetching Scraped Events:${errorModel?.errorMessage}`);
    }
    finally {
      setLoading(false);
    }
  };

  const handleSortColumn = (sortColumn: string, sortType?: SortType) => {
    setFilterReq(pre => ({
      ...pre, 
      orderBy: sortColumn,
      isAscending: sortType == 'asc'
    }));
  };

  return (
    <PageContent>
      <Stack spacing={6} wrap style={{ marginBottom: 12 }}>
        {ScrapSourceList.map(source => (
          <Button
            key={source.value}
            appearance="ghost"
            size="sm"
            href={source.url}
            target="_blank"
            rel="noopener noreferrer"
          >
            {source.label}
          </Button>
        ))}
      </Stack>
      <Stack className="table-toolbar" justifyContent="space-between">
        <Stack spacing={6}>
          <FetchingSelect
            label="Country"
            fetchData={(text, page) => getCountriesLookup(text, page)}
            value={filterReq.countryId}
            onChange={dataKey => setFilterReq(pre => ({...pre, countryId: dataKey}))}
          />               
          <FetchingSelect
            label="City"
            fetchData={(text, page) => getCitiesLookup(text, undefined, page)}
            value={filterReq.cityId}
            onChange={dataKey => setFilterReq(pre => ({...pre, cityId: dataKey}))}
          />
          <SelectPicker
            label="Source"
            data={ScrapSourceList}
            searchable={false}
            value={filterReq.source ?? null}
            onChange={dataKey => setFilterReq(pre => ({ ...pre, source: dataKey ?? undefined }))} 
          />                   
          <SelectPicker
            label="Event Type"
            data={EventTypeList}
            searchable={false}
            value={filterReq.eventType ?? null}
            onChange={dataKey => setFilterReq(pre => ({ ...pre, eventType: dataKey ?? undefined }))} 
          />
          <Input 
            placeholder="Keyword" 
            value={filterReq.keyword ?? ''} 
            onChange={dataKey => setFilterReq(pre => ({...pre, keyword: dataKey}))}
          />
          <Checkbox onChange={(value, checked) => setFilterReq(pre => ({...pre, isApproved: checked === true ? true : undefined}))} >Only Approved</Checkbox>
          <Checkbox onChange={(value, checked) => setFilterReq(pre => ({...pre, isApproved: checked === true ? false : undefined}))} >Only Not Approved</Checkbox>
        </Stack>
      </Stack>

      <Table
        height={Math.max(getHeight(window) - 350, 400)}
        data={items}
        sortColumn={filterReq.orderBy}
        sortType={filterReq.isAscending ? 'asc' : 'desc'}
        onSortColumn={handleSortColumn}
        loading={loading}
      >
        <Column flexGrow={0.5} align="center" >
          <HeaderCell>Image</HeaderCell>
          <ImageCell dataKey="imageUrl" />
        </Column>
        <Column flexGrow={0.5} align="left" sortable>
          <HeaderCell>Source Id</HeaderCell>
          <Cell dataKey="sourceId"/>
        </Column>
        <Column flexGrow={2} align="left" sortable>
          <HeaderCell>Name</HeaderCell>
          <Cell dataKey="name"/>
        </Column>
        <Column flexGrow={1} align="center" sortable>
          <HeaderCell>Country</HeaderCell>
          <Cell dataKey="countryName">
            {(rowData: ScrapedEvent)=> rowData.country?.name ?? rowData.countryName}
          </Cell>
        </Column>
        <Column flexGrow={1} align="center" sortable>
          <HeaderCell>City</HeaderCell>
          <Cell dataKey="cityName">
            {(rowData: ScrapedEvent)=> rowData.city?.name ?? rowData.cityName}
          </Cell>
        </Column>
        <Column flexGrow={1} align="center" sortable>
          <HeaderCell>Start Date</HeaderCell>
          <Cell dataKey="startDate">
            {(rowData: ScrapedEvent) => jsDateToZone(rowData.startDate , rowData.city?.ianaTimeZoneId)}
          </Cell>
        </Column>        
        <Column flexGrow={1} align="center" sortable>
          <HeaderCell>Source</HeaderCell>
          <Cell dataKey="source">
            {(rowData: ScrapedEvent)=> ScrapSourceList.findLast(s => s.value == rowData.source)?.label ?? ''}
          </Cell>
        </Column>
        <Column flexGrow={1} align="center" sortable>
          <HeaderCell>Event Type</HeaderCell>
          <Cell dataKey="source">
            {(rowData: ScrapedEvent)=> EventTypeList.findLast(t => t.value == rowData.eventType)?.label ?? ''}
          </Cell>
        </Column>
        <Column flexGrow={0.5} align="center" >
          <HeaderCell>Is Approved</HeaderCell>
          <Cell style={{ padding: 5 }}>
          {(rowData: ScrapedEvent) => (   
            <Checkbox checked={rowData.isApproved} readOnly></Checkbox>
          )}
          </Cell>
        </Column>        
        <Column flexGrow={0.5} fixed="right" >
        <HeaderCell>...</HeaderCell>
          <Cell style={{ padding: '6px' }}>
            {rowData => (         
              <IconButton as="a" href={`/scraped-events/${rowData.id}`} icon={<EditIcon />} circle size="sm" />  
            )}
          </Cell>
        </Column>

      </Table>
      <Divider />
      <TablePagination 
        totalRecords={pageRes.totalRecords}
        pageSize={pageRes.pageSize}
        activePage={pageRes.pageNumber}
        onChangePage={dataKey => setFilterReq(pre => ({...pre, pageNumber: dataKey}))}
        onChangeLimit={dataKey => setFilterReq(pre => ({...pre, pageSize: dataKey}))}
      />
    </PageContent>
  );
};

export default ScrapedEventPage;
